import React, { useMemo } from "react";
import styled from "styled-components";
import { Coach as CoachModel } from "../model/Coach";
import { Division as DivisionModel, Week as WeekModel } from "../model/Season";
import { useSeasonCalculations } from "../hooks/useSeasonCalculations";
import { INITIAL_ELO } from "../utils/eloCalculations";
import {
    TableContainer,
    TableGrid,
    TableHeader,
    TableHeaderCell,
    TableRow,
    TableCell,
    EmptyCell,
    ValueDisplay,
    ChangeIndicator,
    RankDisplay
} from "./shared/TableComponents";

const EloTrackerSection = styled.div`
    margin-top: 0.5em;
`;

const ChartContainer = styled.div`
    width: 100%;
    margin-bottom: 1em;
    padding: 0.75em;
    border: 1px solid #ddd;
    border-radius: 8px;
    background-color: #fafafa;
`;

const ChartTitle = styled.div`
    font-size: 0.9em;
    font-weight: 600;
    color: #333;
    margin-bottom: 0.5em;
`;

const ChartSvg = styled.svg`
    display: block;
    width: 100%;
    min-height: 200px;
    height: auto;
`;

// Distinct colors for team lines (accessible on light background)
const TEAM_COLORS = [
    '#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd',
    '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf',
];

interface SeasonEloTrackerProps {
    seasonData: {
        divisions: DivisionModel[];
        weeks: WeekModel[];
    };
    leagueCoaches: CoachModel[];
}

export const SeasonEloTracker: React.FC<SeasonEloTrackerProps> = ({
    seasonData,
    leagueCoaches
}) => {
    // Use memoized calculations
    const { eloRatings } = useSeasonCalculations({ seasonData, leagueCoaches });

    // Column headers: Start + regular season weeks only (exclude preseason/postseason)
    const isRegularSeasonWeek = (weekName: string) => {
        const lower = weekName.toLowerCase();
        return !lower.includes('preseason') && !lower.includes('postseason');
    };
    const allWeekNames = [
        'Start',
        ...seasonData.weeks.filter(week => isRegularSeasonWeek(week.weekName)).map(week => week.weekName)
    ];

    // Chart layout and data for ELO line graph (only weeks that have data, so no empty future weeks)
    const chartData = useMemo(() => {
        if (allWeekNames.length < 2 || eloRatings.length === 0) return null;
        let lastIndexWithData = 0;
        for (let i = 1; i < allWeekNames.length; i++) {
            const weekName = allWeekNames[i];
            if (eloRatings.some(r => r.eloHistory.some(h => h.week === weekName))) {
                lastIndexWithData = i;
            }
        }
        const chartWeekNames = allWeekNames.slice(0, lastIndexWithData + 1);

        const marginLeft = 44;
        const marginRight = 140;
        const marginTop = 12;
        const marginBottom = 28;
        const width = 700;
        const height = 300 + Math.min(eloRatings.length * 16, 220);
        const plotWidth = width - marginLeft - marginRight;
        const plotHeight = height - marginTop - marginBottom;

        const allElos = eloRatings.flatMap(r =>
            r.eloHistory.filter(h => chartWeekNames.includes(h.week)).map(h => h.elo)
        );
        const yMin = allElos.length > 0 ? Math.min(INITIAL_ELO, ...allElos) : INITIAL_ELO;
        const yMax = allElos.length > 0 ? Math.max(INITIAL_ELO, ...allElos) : INITIAL_ELO;
        const yPadding = 10;
        const yDomainMin = yMin - yPadding;
        const yDomainMax = yMax + yPadding;
        const yRange = yDomainMax - yDomainMin;

        const xScale = (i: number) =>
            marginLeft + (chartWeekNames.length <= 1 ? 0 : (i / (chartWeekNames.length - 1)) * plotWidth);
        const yScale = (elo: number) =>
            marginTop + plotHeight - ((elo - yDomainMin) / yRange) * plotHeight;

        const series = eloRatings.map((rating, idx) => {
            const points: { x: number; y: number }[] = [];
            chartWeekNames.forEach((weekName, i) => {
                const entry = rating.eloHistory.find(h => h.week === weekName);
                if (entry) points.push({ x: xScale(i), y: yScale(entry.elo) });
            });
            const lastEntry = rating.eloHistory.filter(h => chartWeekNames.includes(h.week)).pop();
            const finalElo = lastEntry?.elo ?? INITIAL_ELO;
            return {
                teamId: rating.teamId,
                coachName: rating.coachName,
                color: TEAM_COLORS[idx % TEAM_COLORS.length],
                points,
                finalElo
            };
        });

        const yGrid10: number[] = [];
        const start10 = Math.ceil(yDomainMin / 10) * 10;
        const end10 = Math.floor(yDomainMax / 10) * 10;
        for (let elo = start10; elo <= end10; elo += 10) yGrid10.push(elo);

        const MIN_LABEL_SPACING = 12;
        const MIN_LABEL_X_GAP = 70;
        const plotTop = marginTop;
        const plotBottom = marginTop + plotHeight;
        const seriesByRank = [...series].sort((a, b) => b.finalElo - a.finalElo);
        const placed: { x: number; y: number }[] = [];
        const labelPositions: { teamId: string; coachName: string; color: string; labelX: number; labelY: number }[] = [];
        const overlaps = (x: number, y: number) =>
            placed.some(p => Math.abs(y - p.y) < MIN_LABEL_SPACING && Math.abs(x - p.x) < MIN_LABEL_X_GAP);
        for (const s of seriesByRank) {
            if (s.points.length === 0) continue;
            const endPoint = s.points[s.points.length - 1];
            let labelX = endPoint.x + 6;
            let labelY = endPoint.y;
            while (overlaps(labelX, labelY)) {
                labelY += MIN_LABEL_SPACING;
            }
            labelY = Math.max(plotTop, Math.min(plotBottom, labelY));
            placed.push({ x: labelX, y: labelY });
            labelPositions.push({ teamId: s.teamId, coachName: s.coachName, color: s.color, labelX, labelY });
        }

        return {
            width,
            height,
            marginLeft,
            marginRight,
            marginTop,
            plotWidth,
            plotHeight,
            series,
            yGrid10,
            yDomainMin,
            yDomainMax,
            xScale,
            yScale,
            chartWeekNames,
            labelPositions
        };
    }, [eloRatings, allWeekNames]);

    return (
        <EloTrackerSection>
            {seasonData.divisions.length > 0 && seasonData.weeks.length > 0 ? (
                <>
                    {chartData && (
                        <ChartContainer>
                            <ChartTitle>ELO over time</ChartTitle>
                            <ChartSvg
                                viewBox={`0 0 ${chartData.width} ${chartData.height}`}
                                preserveAspectRatio="xMidYMid meet"
                                aria-label="ELO ratings by week"
                            >
                                {/* Vertical grid lines at each week */}
                                {chartData.chartWeekNames.map((_, i) => {
                                    const x = chartData.xScale(i);
                                    return (
                                        <line
                                            key={`v-${i}`}
                                            x1={x}
                                            y1={chartData.marginTop}
                                            x2={x}
                                            y2={chartData.marginTop + chartData.plotHeight}
                                            stroke="#ddd"
                                            strokeWidth={0.8}
                                            strokeDasharray="3 3"
                                        />
                                    );
                                })}
                                {/* Horizontal grid lines at every 10 ELO */}
                                {chartData.yGrid10.map(elo => {
                                    const y = chartData.yScale(elo);
                                    const is1200 = elo === INITIAL_ELO;
                                    return (
                                        <line
                                            key={elo}
                                            x1={chartData.marginLeft}
                                            y1={y}
                                            x2={chartData.marginLeft + chartData.plotWidth}
                                            y2={y}
                                            stroke={is1200 ? '#999' : '#ddd'}
                                            strokeWidth={is1200 ? 1.5 : 0.8}
                                            strokeDasharray="3 3"
                                        />
                                    );
                                })}
                                {/* Y-axis labels (every 10 ELO, matching grid lines) */}
                                {chartData.yGrid10.map(elo => (
                                    <text
                                        key={elo}
                                        x={chartData.marginLeft - 6}
                                        y={chartData.yScale(elo) + 4}
                                        textAnchor="end"
                                        fontSize={10}
                                        fill="#555"
                                    >
                                        {elo}
                                    </text>
                                ))}
                                {/* Y-axis label */}
                                <text
                                    x={chartData.marginLeft - 8}
                                    y={chartData.marginTop + chartData.plotHeight / 2}
                                    textAnchor="end"
                                    fontSize={10}
                                    fill="#666"
                                    transform={`rotate(-90, ${chartData.marginLeft - 8}, ${chartData.marginTop + chartData.plotHeight / 2})`}
                                >
                                    ELO
                                </text>
                                {/* Team lines */}
                                {chartData.series.map(s => (
                                    s.points.length > 0 && (
                                        <polyline
                                            key={s.teamId}
                                            points={s.points.map(p => `${p.x},${p.y}`).join(' ')}
                                            fill="none"
                                            stroke={s.color}
                                            strokeWidth={2}
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    )
                                ))}
                                {/* Team labels at end of each line (rank order, overlap nudged down) */}
                                {chartData.labelPositions.map(l => (
                                    <text
                                        key={l.teamId}
                                        x={l.labelX}
                                        y={l.labelY}
                                        textAnchor="start"
                                        dominantBaseline="middle"
                                        fontSize={9}
                                        fill={l.color}
                                        fontWeight={500}
                                    >
                                        {l.coachName}
                                    </text>
                                ))}
                                {/* X-axis week labels */}
                                {chartData.chartWeekNames.map((name, i) => {
                                    const x = chartData.marginLeft + (chartData.chartWeekNames.length <= 1 ? 0 : (i / (chartData.chartWeekNames.length - 1)) * chartData.plotWidth);
                                    return (
                                        <text
                                            key={name}
                                            x={x}
                                            y={chartData.height - 6}
                                            textAnchor="middle"
                                            fontSize={9}
                                            fill="#555"
                                        >
                                            {name === 'Start' ? 'Start' : name.replace(/^Week\s*/i, 'W')}
                                        </text>
                                    );
                                })}
                            </ChartSvg>
                        </ChartContainer>
                    )}
                <TableContainer>
                    <TableGrid $columns={allWeekNames.length + 2}>
                        <TableHeader>
                            <TableHeaderCell>Team</TableHeaderCell>
                            {allWeekNames.map(weekName => (
                                <TableHeaderCell key={weekName}>
                                    {weekName}
                                </TableHeaderCell>
                            ))}
                            <TableHeaderCell>Rank</TableHeaderCell>
                        </TableHeader>

                        {eloRatings.map(rating => (
                            <TableRow key={rating.teamId}>
                                <TableCell>
                                    {rating.coachName}
                                </TableCell>
                                
                                {allWeekNames.map(weekName => {
                                    const historyEntry = rating.eloHistory.find(h => h.week === weekName);
                                    
                                    if (!historyEntry) {
                                        return (
                                            <TableCell key={weekName}>
                                                <EmptyCell>-</EmptyCell>
                                            </TableCell>
                                        );
                                    }

                                    const { elo, change } = historyEntry;
                                    const isPositive = change !== undefined && change > 0;
                                    const isNegative = change !== undefined && change < 0;

                                    return (
                                        <TableCell key={weekName}>
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.1em' }}>
                                                <ValueDisplay>
                                                    {elo}
                                                </ValueDisplay>
                                                {change !== undefined && change !== 0 && (
                                    <ChangeIndicator $isPositive={isPositive} $isNegative={isNegative}>
                                        {change > 0 ? '+' : ''}{change}
                                    </ChangeIndicator>
                                                )}
                                            </div>
                                        </TableCell>
                                    );
                                })}
                                
                                <TableCell>
                                    <RankDisplay $rank={rating.rank || 0}>
                                        {rating.rank}
                                    </RankDisplay>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableGrid>
                </TableContainer>
                </>
            ) : (
                <div style={{ textAlign: 'center', padding: '1em', color: '#666' }}>
                    No season data available for ELO tracking
                </div>
            )}
        </EloTrackerSection>
    );
}; 